import { cn } from '@/lib/cn';

export type WeworkPushSummary = {
  enabled?: boolean;
  planned?: number;
  done?: number;
  pull_wait?: string | null;
  time_ranges_label?: string;
  min_interval_minutes?: number;
};

export type MicroPromotionDaysRow = {
  promotion_channel?: string;
  moments_planned_days?: number;
  groups_planned_days?: number;
  wework_push?: WeworkPushSummary;
};

function normalizeChannel(v?: string): 'moments' | 'groups' | 'all' {
  if (v === 'moments' || v === 'groups' || v === 'all') return v;
  return 'all';
}

function ChannelDaysLine({ label, planned }: { label: string; planned: number }) {
  if (planned <= 0) return null;
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] text-slate-500">{label}</span>
      <span className="font-mono text-sm font-semibold tabular-nums text-slate-800">{planned} 天</span>
    </div>
  );
}

export function MicroPromotionDaysCell({
  row,
  editable = false,
  weworkPushBusy = false,
  onPushWeworkNow,
}: {
  row: MicroPromotionDaysRow;
  editable?: boolean;
  weworkPushBusy?: boolean;
  onPushWeworkNow?: (pullWait: string) => void;
}) {
  const ch = normalizeChannel(row.promotion_channel);
  const showMoments = ch === 'moments' || ch === 'all';
  const showGroups = ch === 'groups' || ch === 'all';
  const mp = Math.max(0, Number(row.moments_planned_days) || 0);
  const gp = Math.max(0, Number(row.groups_planned_days) || 0);
  const wp = row.wework_push;
  const weworkOn = wp?.enabled === true;
  const wPlanned = Math.max(0, Number(wp?.planned) || 0);
  const wDone = Math.max(0, Number(wp?.done) || 0);

  return (
    <div
      className={cn('flex min-w-[72px] flex-col gap-1')}
      title="发圈/群发/Webhook：套餐总天数按区域默认频次折算"
    >
      {showMoments ? <ChannelDaysLine label="发圈" planned={mp} /> : null}
      {showGroups ? <ChannelDaysLine label="群发" planned={gp} /> : null}
      {weworkOn && wPlanned > 0 ? (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] text-slate-500">企微</span>
            <span className="font-mono text-sm font-semibold tabular-nums text-slate-800">
              {wDone}/{wPlanned}
            </span>
          </div>
          {wp?.pull_wait ? (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] leading-snug text-amber-800" title="未到推送间隔">
                {wp.pull_wait}
              </span>
              {editable && onPushWeworkNow ? (
                <button
                  type="button"
                  disabled={weworkPushBusy}
                  title="立即向企微群 Webhook 推送；推送后仍按原频次（如每 2 天 1 次）计算下一次间隔"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPushWeworkNow(wp.pull_wait!);
                  }}
                  className={cn(
                    'inline-flex w-fit items-center rounded border px-1.5 py-px text-[10px] font-medium leading-tight transition-colors',
                    weworkPushBusy
                      ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'
                      : 'border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-300 hover:bg-amber-100',
                  )}
                >
                  {weworkPushBusy ? '推送中…' : '立即推送'}
                </button>
              ) : null}
            </div>
          ) : null}
          {weworkOn && wp?.time_ranges_label ? (
            <span className="text-[10px] text-slate-500" title="允许推送时段">
              时段 {wp.time_ranges_label}
            </span>
          ) : null}
        </div>
      ) : null}
      {!showMoments && !showGroups && !(weworkOn && wPlanned > 0) ? (
        <span className="text-slate-400">—</span>
      ) : null}
    </div>
  );
}
